import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { organizationSignup } from "../api/apiFunction/orgServices";
import {
  INDIVIDUAL_SIGN_UP,
  GOOGLE_AUTH,
} from "../api/apiFunction/authServices";
import { updatePageTitle } from "../utils/titleUtils";
import { LogoWithText } from "../components/Logo";
import AuthShowcase from "../components/pages/auth/AuthShowcase";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    organizationName: "",
    organizationDomain: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isOrgRegister, setIsOrgRegister] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    updatePageTitle("Register");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  // Google returns ID token
  const handleGoogleCallback = async (response) => {
    try {
      // Send the Google token to your backend
      const authResponse = await GOOGLE_AUTH(response.credential);

      if (authResponse.status === 200 && authResponse.data) {
        handleRegistrationSuccess(authResponse.data);
      } else {
        const errorMessage =
          authResponse?.data?.message || "Google authentication failed";
        setError(errorMessage);
        toast.error(errorMessage);
        setLoading(false);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Google authentication failed";
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    setLoading(true);
    setError("");

    // Check if Google Identity Services is loaded
    if (!window.google) {
      setError("Google Sign-In is not available. Please refresh the page.");
      setLoading(false);
      return;
    }

    // Initialize Google Sign-In
    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: handleGoogleCallback,
    });

    // Create a temporary hidden div to render Google button
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.top = "-9999px";
    tempDiv.style.left = "-9999px";
    document.body.appendChild(tempDiv);

    // Render Google button in the hidden div
    window.google.accounts.id.renderButton(tempDiv, {
      theme: "outline",
      size: "large",
    });

    // Click the Google button to trigger popup
    setTimeout(() => {
      const googleButton = tempDiv.querySelector('div[role="button"]');
      if (googleButton) {
        googleButton.click();
      }
      // Clean up
      document.body.removeChild(tempDiv);
    }, 100);

    setLoading(false);
  };

  const handleRegistrationSuccess = async (userData) => {
    // CRITICAL: Check email verification status FIRST
    // If email is not verified, redirect to verification page
    const emailVerified = userData?.emailVerified;
    const userRole = userData?.role;
    const isAdmin =
      userRole === "admin" ||
      userRole === "super admin" ||
      userRole === "super_admin";
    const hasToken = !!userData?.token;

    // If email is not verified and no token (meaning backend didn't allow login), redirect to verification
    if (!isAdmin && emailVerified === false && !hasToken) {
      toast.success("Account created! Please verify your email to continue.", {
        duration: 4000,
      });
      // Redirect to verification page with email
      navigate("/verify-email", {
        state: { email: userData?.email || formData.email },
        replace: true,
      });
      return;
    }

    // If email is verified or user has token, proceed with normal flow
    // Store user session only if token exists (email verified or admin)
    if (userData && hasToken) {
      localStorage.setItem("user", JSON.stringify(userData));
      toast.success("Account created! You're logged in.", { duration: 4000 });
    } else {
      // No token but email might be verified (edge case) - still redirect to verify
      toast.success("Account created! Please verify your email to continue.", {
        duration: 4000,
      });
      navigate("/verify-email", {
        state: { email: userData?.email || formData.email },
        replace: true,
      });
      return;
    }

    // Decide destination based on role / trial / plan (mirrors login flow)
    const role = userData?.role;
    const trial = userData?.trial;
    const planName = userData?.plan?.name || null;

    // Helper: go safely
    const go = (path) => navigate(path, { replace: true });

    if (role === "individual") {
      // Paid plan → chat
      if (planName && planName !== "free") {
        go("/chat");
        return;
      }
      // Trial status based routing
      if (trial?.status === "progress" || trial?.status === "paid") {
        go("/chat");
        return;
      }
      if (trial?.status === "pending") {
        go("/activate-trial");
        return;
      }
      // Missing or expired trial → activate or upgrade
      if (!trial?.startDate || !trial?.endDate) {
        go("/activate-trial");
        return;
      }
      const now = new Date();
      const start = new Date(trial.startDate);
      const end = new Date(trial.endDate);
      if (now >= start && now <= end) {
        go("/chat");
        return;
      }
      go("/upgrade-plan");
      return;
    }

    // Super admin
    if (role === "super_admin" || role === "super admin") {
      go("/admin");
      return;
    }

    // Employee / org roles
    if (role === "employee") {
      if (!userData.plan || userData.plan === "free") {
        go("/upgrade-plan");
        return;
      }
      go("/chat");
      return;
    }

    if (role === "admin" || role === "owner") {
      go("/org/dashboard");
      return;
    }

    // Fallback
    go("/chat");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validate required fields
    if (!formData.name || !formData.email || !formData.password) {
      setError("Name, email, and password are required");
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    // Validate password strength (at least 6 characters)
    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    if (isOrgRegister) {
      // Check if passwords match
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }
      // Validate org fields
      if (!formData.organizationName || !formData.organizationDomain) {
        setError("Organization name and domain are required");
        setLoading(false);
        return;
      }
    }

    try {
      let response;
      if (isOrgRegister) {
        // Prepare the signup payload for organization
        const signupData = {
          ownerName: formData.name,
          organizationName: formData.organizationName,
          organizationDomain: formData.organizationDomain,
          email: formData.email,
          password: formData.password,
        };
        response = await organizationSignup(signupData);
      } else {
        // Prepare the signup payload for individual
        const signupData = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
        };
        response = await INDIVIDUAL_SIGN_UP(signupData);
      }

      // Check for successful response (status 201 for created)
      if (response.status === 201 || response.status === 200) {
        // Individual signup returns { user: {...}, token: "..." } or { user: {...} } (no token if not verified)
        // Organization signup returns { _id, name, email, ..., token: "..." } or { _id, name, email, ... } (no token if not verified)
        let userData = response.data?.user || response.data;
        // If individual signup, include token from root level if it exists
        if (response.data?.user && response.data?.token) {
          userData = { ...response.data.user, token: response.data.token };
        }
        handleRegistrationSuccess(userData);
      } else {
        // Handle API error response
        const errorMessage =
          response?.data?.message || "Registration failed. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "An error occurred during registration";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-bg-50 flex overflow-hidden">
      {/* Left Side - Auth Form */}
      <div className="w-full lg:w-1/2 bg-bg-50 p-4 lg:p-8">
        <div className="bg-bg-70 rounded-3xl h-full flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo */}
            <div className="text-center">
              <LogoWithText className="h-8 w-auto mx-auto mb-8" />
              <div className="leading-tight">
                <h2 className="text-xl font-semibold text-fg-50 mb-1">
                  Create your account and
                </h2>
                <p className="text-xl font-semibold text-fg-50">
                  get started instantly.
                </p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Form */}
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  {isOrgRegister ? "Owner Name" : "Name"}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                  placeholder={isOrgRegister ? "John Doe" : "Your full name"}
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
              {isOrgRegister && (
                <>
                  <div>
                    <label
                      htmlFor="organizationName"
                      className="block text-sm font-medium text-fg-50 mb-2"
                    >
                      Organization Name
                    </label>
                    <input
                      id="organizationName"
                      name="organizationName"
                      type="text"
                      required={isOrgRegister}
                      className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                      placeholder="Acne"
                      value={formData.organizationName}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="organizationDomain"
                      className="block text-sm font-medium text-fg-50 mb-2"
                    >
                      Organization Domain
                    </label>
                    <input
                      id="organizationDomain"
                      name="organizationDomain"
                      type="text"
                      required={isOrgRegister}
                      className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                      placeholder="acne.com"
                      value={formData.organizationDomain}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>
                </>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-fg-50 mb-2"
                >
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-fg-50"
                  >
                    Password
                  </label>
                  {formData.password && formData.password.length < 6 && (
                    <span className="text-xs text-amber-400">
                      At least 6 characters
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    className="w-full px-4 py-2 pr-12 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 focus:ring-ac-02 transition-all duration-200"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    ) : (
                      <Eye
                        strokeWidth={1.5}
                        className="h-5 w-5 text-fg-60 hover:text-fg-50"
                      />
                    )}
                  </button>
                </div>
              </div>
              {isOrgRegister && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-fg-50"
                    >
                      Confirm Password
                    </label>
                    {formData.confirmPassword &&
                      formData.password !== formData.confirmPassword && (
                        <span className="text-xs text-red-400">
                          Passwords don't match
                        </span>
                      )}
                  </div>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required={isOrgRegister}
                      className={`w-full px-4 py-2 pr-12 bg-bg-60 border-0 rounded-lg text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-1 transition-all duration-200 ${
                        formData.confirmPassword &&
                        formData.password !== formData.confirmPassword
                          ? "focus:ring-red-400"
                          : "focus:ring-ac-02"
                      }`}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      disabled={loading}
                    >
                      {showConfirmPassword ? (
                        <EyeOff
                          strokeWidth={1.5}
                          className="h-5 w-5 text-fg-60 hover:text-fg-50"
                        />
                      ) : (
                        <Eye
                          strokeWidth={1.5}
                          className="h-5 w-5 text-fg-60 hover:text-fg-50"
                        />
                      )}
                    </button>
                  </div>
                </div>
              )}
              {/* <div className="mb-4 flex items-center">
                <input
                  id="isOrgRegister"
                  name="isOrgRegister"
                  type="checkbox"
                  checked={isOrgRegister}
                  onChange={() => setIsOrgRegister((prev) => !prev)}
                  className="h-4 w-4 text-ac-02 focus:ring-ac-02 border-gray-300 rounded"
                  disabled={loading}
                />
                <label htmlFor="isOrgRegister" className="ml-2 block text-sm text-fg-50">
                  Register as Organization
                </label>
              </div> */}
              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex justify-center py-2 px-4 rounded-lg text-sm font-medium text-white dark:text-black ${
                    loading
                      ? "bg-ac-02 bg-opacity-25 cursor-not-allowed"
                      : "bg-ac-02 hover:bg-ac-01"
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
                  onClick={handleSubmit}
                >
                  {loading ? "Creating account..." : "Create account"}
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-bd-50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-bg-70 text-fg-60">
                  or continue with
                </span>
              </div>
            </div>

            {/* Google Sign Up */}
            {/* Hidden - Use sign-in page for Google auth instead */}
            {/* <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className={`w-full flex items-center justify-center py-2 px-4 rounded-lg font-medium transition-all duration-200 b ${loading
                  ? "bg-bg-30 cursor-not-allowed"
                  : "bg-bg-60 text-fg-50 hover:bg-bg-50 border border-bd-50"
                }`}
            >
              <svg
                className="w-5 h-5 mr-3"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google
            </button> */}

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-sm text-fg-60">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-medium text-fg-50 hover:text-fg-40"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Showcase */}
      <AuthShowcase />
    </div>
  );
};

export default Register;
